import { useEffect, useState } from 'react';
import {
    Modal,
    Button,
    Dropdown,
    Image,
    Card,
    Row,
    Col,
    OverlayTrigger,
    Tooltip,
} from 'react-bootstrap';
import { Dialog, DialogTitle, DialogPanel } from '@headlessui/react';
import { ICampaignDeliverables, ITemplate } from '../../interfaces/campaign.interface';
import { useAppSelector } from '../../store/store';
import { getOrganizationId } from '../../store/selector/organization.selector';
import { useGetOrganizationTemplatesQuery } from '../../store/api/template.api';
import {
    getTemplateCustomTags,
    getTemplateDefaultTags,
} from '../../store/selector/template.selector';
import { displayErrorToast } from '../../utils/ToastUtils';
import MasonrySkeletonLoader from '../CardLoader';

interface IProps {
    show: boolean;
    onHide: () => void;
    deliverable: ICampaignDeliverables;
    updateDeliverableTagsAndTemplate: (tags: string[], templateDetail: ITemplate | null) => void;
}

const TemplateOrTagsModal = ({
    show,
    onHide,
    deliverable,
    updateDeliverableTagsAndTemplate,
}: IProps) => {
    const organizationId = useAppSelector(getOrganizationId);
    const { data: templates, isLoading } = useGetOrganizationTemplatesQuery(
        organizationId ? organizationId : 0,
        {
            skip: !organizationId,
        },
    );
    const templateDefaultTags = useAppSelector(getTemplateDefaultTags);
    const templateCustomTags = useAppSelector(getTemplateCustomTags);

    const [tagsToShow, setTagsToShow] = useState<string[]>([]);
    const [clickedTemplate, setClickedTemplate] = useState<ITemplate | null>(null);
    const [selectedTags, setSelectedTags] = useState<string[]>(deliverable.tags);

    const handleDone = () => {
        updateDeliverableTagsAndTemplate(selectedTags, clickedTemplate);
        onHide();
    };

    const handleTagClick = (tag: string) => {
        setSelectedTags([tag]);
        setClickedTemplate(null);
    };

    const removeTag = (tag: string) => {
        setSelectedTags(selectedTags.filter((t) => t !== tag));
    };

    useEffect(() => {
        setSelectedTags(deliverable.tags);
        setClickedTemplate(deliverable.templateDetails ? deliverable.templateDetails : null);
    }, [deliverable]);

    useEffect(() => {
        setTagsToShow(() => {
            const uniqueTags = Array.from(new Set([...templateCustomTags, ...templateDefaultTags]));
            return uniqueTags;
        });
    }, [templateCustomTags, templateDefaultTags]);

    return (
        <Dialog
            open={show}
            as="div"
            className="tw-relative tw-z-[2000] tw-focus:outline-none"
            onClose={onHide}
        >
            <div className="tw-fixed tw-inset-0 tw-z-[1100] tw-bg-black/45 " />

            <div className="tw-fixed tw-inset-0 tw-z-[2000] tw-w-screen tw-overflow-y-auto">
                <div className="tw-flex tw-min-h-full tw-items-center tw-justify-center tw-p-4">
                    <DialogPanel className="tw-w-full tw-max-w-[632px] xl:tw-max-w-[1264px] tw-rounded-xl tw-bg-white tw-p-4">
                        <DialogTitle as="h3" className="tw-text-base/7 tw-font-medium">
                            Select Template or Tags
                        </DialogTitle>
                        <div className="tw-flex tw-flex-row tw-items-center tw-mt-2">
                            <div>
                                <div className="alert alert-light mb-3 mt-0">
                                    <strong>Heads up!</strong> Select tags or specific template. If
                                    you select specific template, that template will be used, or if
                                    you just select tags we will filter templates based on the tags
                                    selected
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px', // Add space between the dropdown and the selected tags
                                    }}
                                >
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            variant="secondary"
                                            id="dropdown-basic"
                                            size="sm"
                                        >
                                            Select Tag
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {tagsToShow.map((tag) => (
                                                <Dropdown.Item
                                                    key={tag}
                                                    onClick={() => handleTagClick(tag)}
                                                >
                                                    {tag}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>

                                    {selectedTags.length > 0 && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '5px',
                                                padding: '5px',
                                                borderRadius: '4px',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {selectedTags.map((tag) => (
                                                <span
                                                    key={tag}
                                                    style={{
                                                        backgroundColor: '#e0e0e0',
                                                        color: '#333',
                                                        padding: '3px 8px',
                                                        borderRadius: '15px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '5px',
                                                    }}
                                                >
                                                    {tag}
                                                    <button
                                                        type="button"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            border: 'none',
                                                            color: '#777',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                        }}
                                                        onClick={() => removeTag(tag)}
                                                    >
                                                        &times;
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <>
                                    {isLoading ? (
                                        <Row
                                            className="g-3 overflow-auto no-scroll mt-1"
                                            style={{ height: '475px' }}
                                        >
                                            <MasonrySkeletonLoader
                                                items={9}
                                                cardWidth={340}
                                                cardHeight={120}
                                            />
                                        </Row>
                                    ) : (
                                        <Row
                                            className="g-3 overflow-auto no-scroll mt-1"
                                            style={{ height: '475px' }}
                                        >
                                            <Col xs={4}>
                                                <Card
                                                    style={{
                                                        cursor: 'pointer',
                                                        height: '200px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#f0f8ff',
                                                    }}
                                                    className={`text-center shadow-sm pixii-choice template-card ${
                                                        clickedTemplate === null
                                                            ? 'border-primary selected border-2'
                                                            : 'border-0'
                                                    }`}
                                                >
                                                    <Card.Body
                                                        className="p-2 d-flex flex-column align-items-center justify-content-center"
                                                        onClick={() => setClickedTemplate(null)}
                                                    >
                                                        <i
                                                            className="ph ph-sparkle mb-2"
                                                            style={{
                                                                fontSize: '5rem',
                                                                color: '#6f4e84',
                                                            }}
                                                        ></i>
                                                        <Card.Text className="fw-bold text-primary">
                                                            Pixii Choice
                                                        </Card.Text>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            {templates &&
                                                templates.map((template, index) => {
                                                    if (
                                                        template.platform ===
                                                            deliverable.platform &&
                                                        template.asset_type ===
                                                            deliverable.asset_type &&
                                                        (selectedTags.length === 0 ||
                                                            template.tags.indexOf(
                                                                selectedTags[0],
                                                            ) !== -1)
                                                    ) {
                                                        return (
                                                            <Col xs={4} key={template.id}>
                                                                <OverlayTrigger
                                                                    placement="auto"
                                                                    delay={{ show: 150, hide: 400 }}
                                                                    overlay={
                                                                        <Tooltip>
                                                                            {template.name}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <Card
                                                                        className={`text-centershadow-sm template-card ${
                                                                            clickedTemplate &&
                                                                            clickedTemplate.id ===
                                                                                template.id
                                                                                ? 'border-primary selected'
                                                                                : 'border-0'
                                                                        }`}
                                                                        onClick={() =>
                                                                            setClickedTemplate(
                                                                                template,
                                                                            )
                                                                        }
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            height: '216px',
                                                                        }} // Consistent height for all template cards
                                                                    >
                                                                        <Card.Body className="p-2 d-flex flex-column align-items-center justify-content-center">
                                                                            <Image
                                                                                src={
                                                                                    template.preview_url
                                                                                }
                                                                                alt={template.name}
                                                                                fluid
                                                                                style={{
                                                                                    borderRadius:
                                                                                        '8px',
                                                                                    width: '100%',
                                                                                    height: '150px',
                                                                                    objectFit:
                                                                                        'cover',
                                                                                }}
                                                                            />
                                                                            <Card.Text
                                                                                style={{
                                                                                    width: '90%',
                                                                                    whiteSpace:
                                                                                        'nowrap',
                                                                                    overflow:
                                                                                        'hidden',
                                                                                    textOverflow:
                                                                                        'ellipsis',
                                                                                }}
                                                                                className="mt-1 p-2"
                                                                            >
                                                                                {template.name}
                                                                            </Card.Text>
                                                                        </Card.Body>
                                                                    </Card>
                                                                </OverlayTrigger>
                                                            </Col>
                                                        );
                                                    } else {
                                                        return <></>;
                                                    }
                                                })}
                                        </Row>
                                    )}
                                </>
                            </div>
                            <div className="tw-hidden xl:tw-block tw-shrink-0 tw-pt-2 tw-px-4 tw-w-[600px] tw-h-full tw-max-h-[637px] tw-overflow-auto">
                                {clickedTemplate ? (
                                    <div>
                                        <Image
                                            src={clickedTemplate.preview_url}
                                            fluid
                                            className="tw-w-full tw-h-full tw-rounded-l-md"
                                        />
                                    </div>
                                ) : (
                                    <div className="tw-h-full tw-w-full tw-flex tw-flex-col tw-gap-y-5 tw-items-center tw-justify-center">
                                        <i className="ph-bold ph-image-square tw-text-4xl tw-text-purple-100"></i>
                                        <h4 className="tw-text-lg tw-font-medium tw-text-gray-800 tw-mb-2">
                                            Select a template to see preview
                                        </h4>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="tw-mt-4 tw-flex tw-items-center tw-justify-end tw-gap-x-2">
                            <Button variant="secondary" onClick={onHide}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleDone}>
                                Select
                            </Button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};

export default TemplateOrTagsModal;
